import type { ChecklistMediaTerminalEvent } from './ChecklistMediaTerminalEvents';
import { observationKey } from './checklistObservations';

type Draft = Record<string, string>;

/** Apenas a prévia salva dessa tentativa acompanha o término; edição posterior continua protegida. */
export const reconcileChecklistMediaDraft = (draft: Draft, dirty: Draft, points: readonly unknown[],
  event: ChecklistMediaTerminalEvent): Draft | null => {
  const current = draft[event.fieldId];
  if (points.length || dirty[event.fieldId] !== undefined ||
      (current !== event.expectedValue && current !== event.localUri)) return null;
  const changes: Draft = { [event.fieldId]: event.uri };
  const noteKey = observationKey(event.fieldId);
  if (dirty[noteKey] === undefined && (draft[noteKey] || null) === event.expectedObservation) {
    changes[noteKey] = event.observation ?? '';
  }
  return changes;
};

export const isTemporaryLocalBusy = (error: unknown): boolean =>
  /database is locked|sqlite_busy/i.test(String(error instanceof Error ? error.message : error));

/** Buffer da tela: só remove uma alteração depois de confirmar seu valor no banco. */
export class ChecklistDraftPersistence {
  readonly dirty: Draft = {};
  private active: Promise<void> | null = null;
  private stopped = false;

  change(id: string, value: string): void { this.dirty[id] = value; }
  hasPending(): boolean { return Object.keys(this.dirty).length > 0; }
  stop(): void { this.stopped = true; }
  start(): void { this.stopped = false; }

  flush(save: (changes: Draft) => Promise<void>, confirmed: (changes: Draft) => void): Promise<void> {
    if (this.active) return this.active;
    const run = async () => {
      let deadline = Date.now() + 3000;
      let retries = 0;
      while (this.hasPending() && !this.stopped) {
        const changes = { ...this.dirty };
        try {
          await save(changes);
        } catch (error) {
          const delay = [50, 150, 300, 500, 1000, 800][Math.min(retries++, 5)];
          if (!isTemporaryLocalBusy(error) || Date.now() + delay >= deadline || this.stopped) throw error;
          await new Promise(resolve => setTimeout(resolve, delay));
          if (Date.now() >= deadline || this.stopped) throw error;
          continue;
        }
        for (const [id, value] of Object.entries(changes)) {
          if (this.dirty[id] === value) delete this.dirty[id];
        }
        confirmed(changes);
        // Confirmação é progresso; só a sequência sem progresso consome o orçamento de busy.
        deadline = Date.now() + 3000;
        retries = 0;
      }
      if (this.hasPending()) throw new Error('Salvamento interrompido ao sair da tela.');
    };
    this.active = run().finally(() => { this.active = null; });
    return this.active;
  }
}

/** Refresh antigo não substitui edição pendente nem confirmação local ainda não observada pela leitura. */
export const mergeChecklistDraftRefresh = (current: Draft, previous: Draft, incoming: Draft, unobserved: Draft,
  readAfterConfirmation = false) => {
  const base = { ...incoming };
  for (const [id, value] of Object.entries(unobserved)) {
    if (readAfterConfirmation || incoming[id] === value) delete unobserved[id];
    else base[id] = value;
  }
  const draft = { ...base };
  for (const [id, value] of Object.entries(current)) {
    if (value !== previous[id]) draft[id] = value;
  }
  return { draft, confirmed: base };
};
